import type { CallHeardMarker } from '@compass/client/types/call';

import type { FieldMetadata, HeardSeek } from '@/components/fields/types';
import {
  LEAD_IN_SECONDS,
  resolveHeardOffsetSeconds,
  snapToTranscriptSegment,
} from '@/utils/heardSeek';
import type { TranscriptSegment } from '@/utils/transcript';

/**
 * Build a per-field "heard seek" descriptor for each heard marker: the call id plus the
 * transcript-snapped time range to jump to. Keyed by the marker's field key (e.g.
 * "member_id" or "drug[0].coverage.coverage_status"); mergeHeardSeek then places it where
 * the field renderers read it. Ids + timestamps only — never field values (PHI-safe).
 */
export function buildHeardSeek(
  markers: Record<string, CallHeardMarker> | undefined,
  segments: TranscriptSegment[],
  callStartedMs: number | undefined,
  callId: string | undefined,
): Record<string, HeardSeek> {
  const out: Record<string, HeardSeek> = {};
  if (!markers || !callId) return out;
  for (const [key, marker] of Object.entries(markers)) {
    const offset = resolveHeardOffsetSeconds(marker, callStartedMs);
    const snap = offset != null ? snapToTranscriptSegment(offset, segments, LEAD_IN_SECONDS) : null;
    const hs: HeardSeek = { source_id: callId };
    if (snap) {
      hs.time_start = String(snap.seekTo);
      hs.time_end = String(snap.range.end);
    } else if (offset != null) {
      hs.time_start = String(Math.max(0, offset - LEAD_IN_SECONDS));
    }
    out[key] = hs;
  }
  return out;
}

/** "drug[0].coverage.coverage_status" -> ["drug", 0, "coverage", "coverage_status"] */
function parseKeyPath(key: string): Array<string | number> {
  const path: Array<string | number> = [];
  for (const token of key.split('.')) {
    const m = /^(.+?)\[(\d+)\]$/.exec(token);
    if (m) {
      path.push(m[1], Number(m[2]));
    } else {
      path.push(token);
    }
  }
  return path;
}

/**
 * Set `heardSeek` at a nested path inside `fields`. NOTE: this MUTATES the passed `fields`
 * object in place — it does not clone the top node. Safe because the only caller
 * (`mergeHeardSeek`) hands in a fresh shallow copy of `base.fields`, and this clones each
 * DESCENDANT node it steps into, so no node of the caller's ORIGINAL base metadata is ever
 * mutated. Numeric segments build arrays so the result matches the per-item tree the
 * entity-array renderers read (fields[entity][index]...heardSeek -- see DrugOutputField /
 * getFieldHeardSeek).
 */
function setHeardSeekAtPath(
  fields: Record<string, any>,
  path: Array<string | number>,
  hs: HeardSeek,
): void {
  let node: any = fields;
  for (let i = 0; i < path.length; i += 1) {
    const seg = path[i];
    if (i === path.length - 1) {
      node[seg] = { ...(node[seg] ?? {}), heardSeek: hs };
    } else {
      const childIsArray = typeof path[i + 1] === 'number';
      const existingChild = node[seg];
      if (childIsArray) {
        node[seg] = Array.isArray(existingChild) ? [...existingChild] : [];
      } else {
        node[seg] = existingChild && typeof existingChild === 'object' ? { ...existingChild } : {};
      }
      node = node[seg];
    }
  }
}

/**
 * Merge heard-seek descriptors into output FieldMetadata under a `heardSeek` node so the
 * dedicated heard affordance can render — independent of extraction evidence. Bracket keys
 * (entity arrays like "drug[0].coverage.coverage_status") go into the nested per-item tree
 * the array renderers read; flat scalar keys (e.g. "member_id") stay flat.
 */
export function mergeHeardSeek(
  base: FieldMetadata | undefined,
  heard: Record<string, HeardSeek>,
): FieldMetadata | undefined {
  if (Object.keys(heard).length === 0) return base;
  const fields: Record<string, any> = { ...(base?.fields ?? {}) };
  for (const [key, hs] of Object.entries(heard)) {
    if (key.includes('[')) {
      setHeardSeekAtPath(fields, parseKeyPath(key), hs);
    } else {
      fields[key] = { ...(fields[key] ?? {}), heardSeek: hs };
    }
  }
  return { ...(base ?? {}), fields } as FieldMetadata;
}
